import type Phaser from "phaser";
import { ICON_ART_SIZE, type IconPlace } from "odyssey-shared";

/**
 * The soft dark outline another character's owned ground item is drawn on (P2.8-T74, the author's answer of
 * 2026-10-10, "Dim less, add shadow"): a ring around the icon's own shape, outside its opaque pixels only, so it darkens
 * the ground around the item and never the item. The ring fades outward: the pixels next to the icon at
 * `OUTLINE_ALPHAS[0]`, the next ones out at `OUTLINE_ALPHAS[1]`. One texture is made per icon key, the first time an
 * item of that icon is drawn owned, from the page's icon atlas image; an image of it is placed only when the ground
 * items are placed again, so a render frame does nothing for it.
 */

/**
 * How far the outline reaches past the icon's shape, in the icon's pixels: two, one world pixel at the icon's half
 * scale.
 */
export const OUTLINE_WIDTH = 2;

/**
 * The outline's strength at each distance from the icon's shape, nearest first.
 */
export const OUTLINE_ALPHAS: readonly number[] = [0.7, 0.35];

/**
 * The outline's colour: the design's `--ot-ink-900`, its darkest ink.
 */
const OUTLINE_COLOUR = "#07080b";

/**
 * The alpha below which an icon pixel counts as clear.
 */
const OPAQUE_FROM = 128;

/**
 * The outline of an icon's shape: for each pixel of a square `OUTLINE_WIDTH` wider than the icon on every side, the
 * outline's strength there, 0 on and inside the shape and beyond the ring. A pixel's distance is the Chebyshev distance
 * to the nearest opaque pixel, so the ring keeps the pixel art's square corners.
 *
 * @param rgba The icon's pixels, `size` by `size`, four bytes a pixel.
 * @param size The icon's side in pixels.
 * @returns The strengths, row by row, `size + 2 * OUTLINE_WIDTH` square.
 * @example
 * outlineAlphas(new Uint8ClampedArray([0, 0, 0, 255]), 1);
 * // a 5 by 5 square: 0 at the middle, 0.7 on the ring around it, 0.35 on the outer ring
 */
export function outlineAlphas(rgba: Uint8ClampedArray, size: number): Float32Array {
  const side = size + OUTLINE_WIDTH * 2;
  const alphas = new Float32Array(side * side);
  const opaque = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < size && y < size && (rgba[(y * size + x) * 4 + 3] as number) >= OPAQUE_FROM;
  for (let y = 0; y < side; y += 1) {
    for (let x = 0; x < side; x += 1) {
      const ix = x - OUTLINE_WIDTH;
      const iy = y - OUTLINE_WIDTH;
      if (opaque(ix, iy)) {
        continue;
      }
      for (let distance = 1; distance <= OUTLINE_WIDTH; distance += 1) {
        if (nearOpaque(opaque, ix, iy, distance)) {
          alphas[y * side + x] = OUTLINE_ALPHAS[distance - 1] as number;
          break;
        }
      }
    }
  }
  return alphas;
}

/**
 * Whether an opaque pixel lies within a Chebyshev distance of a point.
 *
 * @param opaque Whether a pixel of the icon is opaque.
 * @param x The point's column in the icon's pixels.
 * @param y Its row.
 * @param distance The distance.
 * @returns True when one does.
 */
function nearOpaque(opaque: (x: number, y: number) => boolean, x: number, y: number, distance: number): boolean {
  for (let dy = -distance; dy <= distance; dy += 1) {
    for (let dx = -distance; dx <= distance; dx += 1) {
      if (opaque(x + dx, y + dy)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * The texture key of an icon's outline.
 *
 * @param key The icon key.
 * @returns The texture key.
 */
export function outlineKey(key: string): string {
  return `ground:outline:${key}`;
}

/**
 * Makes an icon's outline texture once, read from the atlas image at the icon's place.
 *
 * @param textures The scene's texture manager.
 * @param image The decoded icon atlas, loaded with CORS so its pixels can be read, as its texture upload needs anyway.
 * @param key The icon key.
 * @param place Where the icon stands in the atlas.
 * @returns The texture key, or null when no canvas could be made.
 */
export function outlineTexture(textures: Phaser.Textures.TextureManager, image: HTMLImageElement, key: string, place: IconPlace): string | null {
  const name = outlineKey(key);
  if (textures.exists(name)) {
    return name;
  }
  const side = ICON_ART_SIZE + OUTLINE_WIDTH * 2;
  const canvas = textures.createCanvas(name, side, side);
  if (canvas === null) {
    return null;
  }
  const context = canvas.getContext();
  context.drawImage(image, place.x, place.y, ICON_ART_SIZE, ICON_ART_SIZE, 0, 0, ICON_ART_SIZE, ICON_ART_SIZE);
  const alphas = outlineAlphas(context.getImageData(0, 0, ICON_ART_SIZE, ICON_ART_SIZE).data, ICON_ART_SIZE);
  context.clearRect(0, 0, side, side);
  context.fillStyle = OUTLINE_COLOUR;
  for (let i = 0; i < alphas.length; i += 1) {
    const alpha = alphas[i] as number;
    if (alpha > 0) {
      context.globalAlpha = alpha;
      context.fillRect(i % side, Math.floor(i / side), 1, 1);
    }
  }
  context.globalAlpha = 1;
  canvas.refresh();
  return name;
}
